import { CsvImportRow } from 'src/services/common.types';
import { CustomFieldType, ICustomFieldConfig } from 'src/services/custom-fields/custom-field.types';

import {
  ImportField,
  FULL_NAME_KEY,
  fieldLabelKey,
  ImportFieldGroup,
  ImportRequirement,
  ImportEntityConfig,
} from './import-fields';

/** Marks a mapping target as a custom field; the config id follows. */
export const CUSTOM_FIELD_TARGET_PREFIX = 'cf:';

/** One choice in a column's "Import as" dropdown: a standard field or a custom field. */
export interface ImportTarget {
  /** The field key, or `cf:<config id>` for a custom field. */
  id: string;
  label: string;
  group: ImportFieldGroup | 'custom';
  aliases: string[];
  hint?: string;
  example?: string;
  hiddenFromTemplate?: boolean;
}

/** Column index → target id. An empty string leaves the column out of the import. */
export type ImportMapping = Record<number, string>;

type Translate = (key: string, options?: any) => string;

/** Lower-cased with everything but letters and digits removed, so headings compare loosely. */
export const normalizeHeader = (value: string): string =>
  (value ?? '').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

/** A header cell as shown to the user: trimmed, without the byte-order mark Excel writes. */
export const displayHeader = (value: string): string => String(value ?? '').replace(/^\uFEFF/, '').trim();

const customFieldOptions = (config: ICustomFieldConfig): string[] =>
  (config.select_options ?? [])
    .filter((option) => !option.archived_at)
    .map((option) => option.label);

function customFieldHint(config: ICustomFieldConfig, t: Translate): string | undefined {
  const options = customFieldOptions(config).join(', ');

  switch (config.field_type) {
    case CustomFieldType.SELECT:
      return t('csv_import.hints.custom_select', { options });
    case CustomFieldType.MULTI_SELECT:
      return t('csv_import.hints.custom_multi_select', { options });
    case CustomFieldType.BOOLEAN:
      return t('csv_import.hints.custom_boolean');
    default:
      return undefined;
  }
}

function customFieldExample(config: ICustomFieldConfig): string {
  if (config.field_type === CustomFieldType.BOOLEAN) return 'Yes';
  return customFieldOptions(config)[0] ?? '';
}

/** Every target the mapper offers for a record type: its standard fields, then its custom fields. */
export function buildImportTargets(
  config: ImportEntityConfig,
  customFields: ICustomFieldConfig[] | undefined,
  t: Translate
): ImportTarget[] {
  const standard = config.fields.map((field: ImportField): ImportTarget => ({
    id: field.key,
    label: t(`csv_import.fields.${fieldLabelKey(field)}`),
    group: field.group,
    aliases: field.aliases ?? [],
    hint: field.hint ? t(`csv_import.hints.${fieldLabelKey(field)}`) : undefined,
    example: field.example,
    hiddenFromTemplate: field.hiddenFromTemplate,
  }));

  const custom = (customFields ?? []).map((customField): ImportTarget => ({
    id: `${CUSTOM_FIELD_TARGET_PREFIX}${customField.id}`,
    label: customField.label,
    group: 'custom',
    aliases: [],
    hint: customFieldHint(customField, t),
    example: customFieldExample(customField),
  }));

  return [...standard, ...custom];
}

/**
 * Pre-selects a target for each column whose heading names one. Own names are matched before
 * aliases, so an alias of one field never takes a column headed with another field's name, and
 * each target is used at most once. Unrecognised columns are left out of the import.
 */
export function autoMatch(headers: string[], targets: ImportTarget[]): ImportMapping {
  const mapping: ImportMapping = {};
  const used = new Set<string>();
  const normalized = headers.map((header) => normalizeHeader(displayHeader(header)));

  const assign = (candidates: (target: ImportTarget) => string[]) => {
    normalized.forEach((header, index) => {
      if (!header || mapping[index]) return;

      const match = targets.find(
        (target) => !used.has(target.id) && candidates(target).some((candidate) => normalizeHeader(candidate) === header)
      );

      if (match) {
        mapping[index] = match.id;
        used.add(match.id);
      }
    });
  };

  assign((target) => [target.label, target.id]);
  assign((target) => target.aliases);

  headers.forEach((_, index) => {
    if (mapping[index] === undefined) mapping[index] = '';
  });

  return mapping;
}

/** Expands the full-name alternative into the two fields it produces once split. */
const requiredKeys = (alternative: string[]): string[] =>
  alternative.reduce<string[]>(
    (keys, key) => keys.concat(key === FULL_NAME_KEY ? ['first_name', 'last_name'] : [key]),
    []
  );

/** The requirements a mapping does not yet satisfy. Empty means it is ready to import. */
export function unmetRequirements(mapping: ImportMapping, config: ImportEntityConfig): ImportRequirement[] {
  const mapped = new Set(Object.values(mapping).filter(Boolean));

  return config.requirements.filter(
    (requirement) => !requirement.alternatives.some((alternative) => alternative.every((key) => mapped.has(key)))
  );
}

/** Splits a full name on its last space, keeping a compound first name together. */
export function splitFullName(value: string): { first_name?: string; last_name?: string } {
  const parts = value.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return {};
  if (parts.length === 1) return { first_name: parts[0] };

  return { first_name: parts.slice(0, -1).join(' '), last_name: parts[parts.length - 1] };
}

/**
 * Turns the file's data rows into import rows using the mapping. Blank lines are dropped quietly;
 * a row missing a required value is dropped too, but reported by its line number in the file so the
 * user can fix it — the old importer discarded those without a word.
 */
export function buildRows(
  dataRows: unknown[][],
  mapping: ImportMapping,
  config: ImportEntityConfig
): { rows: CsvImportRow[]; skippedLines: number[] } {
  const rows: CsvImportRow[] = [];
  const skippedLines: number[] = [];

  dataRows.forEach((cells, index) => {
    const values = (cells ?? []).map((cell) => String(cell ?? '').trim());
    if (values.every((value) => !value)) return;

    const row: Record<string, string> = {};
    const customFields: Record<string, string> = {};

    Object.keys(mapping).forEach((column) => {
      const target = mapping[Number(column)];
      const value = values[Number(column)];
      if (!target || !value) return;

      if (target.startsWith(CUSTOM_FIELD_TARGET_PREFIX)) {
        customFields[target.slice(CUSTOM_FIELD_TARGET_PREFIX.length)] = value;
      } else {
        row[target] = value;
      }
    });

    if (row[FULL_NAME_KEY]) {
      const { first_name: firstName, last_name: lastName } = splitFullName(row[FULL_NAME_KEY]);
      if (!row.first_name && firstName) row.first_name = firstName;
      if (!row.last_name && lastName) row.last_name = lastName;
      delete row[FULL_NAME_KEY];
    }

    const complete = config.requirements.every((requirement) =>
      requirement.alternatives.some((alternative) => requiredKeys(alternative).every((key) => !!row[key]))
    );

    if (!complete) {
      // Line 1 is the header row.
      skippedLines.push(index + 2);
      return;
    }

    rows.push(Object.keys(customFields).length > 0 ? { ...row, custom_fields: customFields } : row);
  });

  return { rows, skippedLines };
}

/** The first few non-blank values of a column, to help the user recognise it. */
export function sampleValues(dataRows: unknown[][], column: number, count = 3): string[] {
  const samples: string[] = [];

  dataRows.some((cells) => {
    const value = String(cells?.[column] ?? '').trim();
    if (value) samples.push(value);
    return samples.length >= count;
  });

  return samples;
}

const escapeCsvCell = (value: string): string =>
  /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/**
 * A template file: one column per target, headed with the label the mapper matches on, and one
 * example row. The byte-order mark makes Excel read it as UTF-8, so names with accents survive.
 */
export function buildTemplateCsv(targets: ImportTarget[]): string {
  const columns = targets.filter((target) => !target.hiddenFromTemplate);

  const lines = [columns.map((target) => target.label), columns.map((target) => target.example ?? '')];

  return `\uFEFF${lines.map((line) => line.map(escapeCsvCell).join(',')).join('\r\n')}`;
}
